import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map, pick, flatten, get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ShareThisChart } from 'client/site-modules/shared/components/share-this-chart/share-this-chart';
import { USMap } from 'client/site-modules/shared/components/us-map/us-map';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ViewSelector } from 'client/site-modules/infographics/components/view-selector/view-selector';
import { MapLegend } from 'site-modules/shared/components/infographics/map-legend/map-legend';
import { StateCard } from 'site-modules/shared/components/infographics/state-card/state-card';
import { NationalSalesList } from 'client/site-modules/infographics/components/national-sales-list/national-sales-list';
import { Insights } from 'client/site-modules/infographics/components/insights/insights';
import { BSC_CONTENT_PATH, VEHICLE_VIEW_OPTION } from 'client/site-modules/infographics/constants/best-selling-cars';
import { StateCardVehiclesContent } from 'client/site-modules/infographics/components/state-card-vehicles-content/state-card-vehicles-content';

import './best-selling-cars.scss';

const PAGE_DISPLAY_NAME = 'Most Popular Cars';
const PAGE_URL = 'https://www.edmunds.com/most-popular-cars/';

const NAMES_TO_COLOR_CODES = {
  blue: '#007ee5',
  'sea-green': '#36b37e',
  violet: '#6554c0',
  yellow: '#ffbf00',
  wine: '#8c1a16',
  red: '#ff3030',
  'dark-blue': '#00374d',
  orange: '#ff5630',
  green: '#3b6600',
  magenta: '#d50661',
  gray: '#999999',
};

export function getVehiclesColorMap(colorsMappingEntry) {
  const colorMap = {};
  colorsMappingEntry.children().forEach(entry => {
    const { vehicleName, color } = entry.getAllMetadata();
    colorMap[vehicleName] = color;
  });
  return colorMap;
}

export function getStatesColorMap(statesData, vehiclesColorMap) {
  const colorMap = {};
  statesData.forEach(state => {
    const { bestSeller, stateCode } = state;
    const vehicleColor = NAMES_TO_COLOR_CODES[vehiclesColorMap[bestSeller]];
    if (!colorMap[vehicleColor]) {
      colorMap[vehicleColor] = [stateCode];
    } else {
      colorMap[vehicleColor].push(stateCode);
    }
  });
  return colorMap;
}

export class BestSellingCarsUI extends Component {
  static propTypes = {
    content: CmsEntities.Content,
    isWidget: PropTypes.bool,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    content: DEFAULT_CONTENT,
    isWidget: false,
    isMobile: false,
  };

  state = {
    selectedView: VEHICLE_VIEW_OPTION,
    isStateCardOpened: false,
    selectedVehicle: null,
    selectedState: null,
  };

  onViewChange = tabValue => {
    this.closeStateCard();

    this.setState({
      selectedView: tabValue,
      selectedVehicle: null,
      selectedState: null,
    });
  };

  onVehicleChange = vehicle => {
    const { selectedVehicle } = this.state;

    this.closeStateCard();
    this.setState({ selectedVehicle: vehicle.name === selectedVehicle ? null : vehicle.name, selectedState: null });
  };

  onStateChange = state => {
    this.openStateCard();
    this.setSelectedState(state.stateCode);
  };

  onMapStateClick = clickedStateId => {
    this.openStateCard();
    this.setSelectedState(clickedStateId);
  };

  setSelectedState = stateId => {
    this.setState({ selectedState: stateId, selectedVehicle: null });
  };

  openStateCard = () => this.setState({ isStateCardOpened: true });

  closeStateCard = () => this.setState({ isStateCardOpened: false, selectedState: null });

  render() {
    const { content, isWidget, isMobile } = this.props;
    const { selectedView, selectedVehicle, selectedState, isStateCardOpened } = this.state;
    const { title = PAGE_DISPLAY_NAME } = content;
    const breadcrumbs = [getHomeBreadcrumb(), getBaseBreadcrumb(title)];
    const isVehicleViewEnabled = selectedView === VEHICLE_VIEW_OPTION;
    const nationalSalesData = content.child('national-sales');
    const nationalSalesVehiclesList = nationalSalesData
      .children()
      .map(vehicle => ({ ...vehicle.getAllMetadata(), id: vehicle.id }));
    const stateOptions = content
      .child('state-vehicle-sales')
      .children()
      .map(state => ({ ...state.getAllMetadata(), stateCode: state.id.split('-')[1] }));
    const vehiclesColorMap = getVehiclesColorMap(content.child('colors-mapping'));

    const vehiclesOptions = map(vehiclesColorMap, (color, vehicle) => ({
      name: vehicle,
      color: NAMES_TO_COLOR_CODES[color],
    }));

    const selectProps = isVehicleViewEnabled
      ? {
          toggle: 'Choose a vehicle',
          options: vehiclesOptions,
          onChange: this.onVehicleChange,
          value: selectedVehicle,
          name: 'select-vehicle',
          valueKey: 'name',
          labelKey: 'name',
        }
      : {
          toggle: 'Choose a state',
          options: stateOptions,
          onChange: this.onStateChange,
          value: selectedState,
          name: 'select-state',
          valueKey: 'stateCode',
          labelKey: 'state',
        };
    const selectedStateData = stateOptions.find(state => state.stateCode === selectedState);

    const showStateCardAboveMap = isVehicleViewEnabled || isMobile;

    const shareableWidgetData = {
      widgetName: 'most-popular-cars',
      defaultWidth: 425,
      defaultHeight: 720,
      minWidth: 290,
      minHeight: 600,
      defaultTitle: title,
    };

    let statesColorMap = getStatesColorMap(stateOptions, vehiclesColorMap);

    if (selectedVehicle) {
      statesColorMap = {
        [NAMES_TO_COLOR_CODES.gray]: flatten(Object.values(statesColorMap)),
        ...pick(statesColorMap, [NAMES_TO_COLOR_CODES[vehiclesColorMap[selectedVehicle]]]),
      };
    }

    if (selectedState) {
      statesColorMap = {
        [NAMES_TO_COLOR_CODES.gray]: flatten(Object.values(statesColorMap)),
        [NAMES_TO_COLOR_CODES.blue]: [selectedState],
      };
    }

    const selectedStateName = get(selectedStateData, 'state', '');
    const isStateCardOpen = !!(isStateCardOpened && selectedStateData);

    return (
      <main id="main-content" tabIndex={-1} className="best-selling-cars">
        <Container>
          {!isWidget && <Breadcrumbs className="ps-0 mb-2" data={breadcrumbs} />}
          <div className="d-flex justify-content-between">
            <h1 className="heading-2 mb-1">{title}</h1>
            <ShareThisChart pageTitle={title} pageUrl={PAGE_URL} shareableWidgetData={shareableWidgetData} />
          </div>
          {!isWidget && (
            <Row className="mb-1_5">
              <Col xs={12} xl={9}>
                <ContentFragment componentToUse="div" classes="page-content">
                  {content.child('html').content}
                </ContentFragment>
              </Col>
            </Row>
          )}
          <ViewSelector onViewChange={this.onViewChange} selectedView={selectedView} selectProps={selectProps} />
          <Row className="my-1_5">
            <Col xs={12} xl={9}>
              <USMap
                statesColorMap={statesColorMap}
                isClickable
                onMapStateClick={this.onMapStateClick}
                isMobile={isMobile}
              />
              {showStateCardAboveMap && (
                <StateCard
                  cardTitle={selectedStateName}
                  pageTitle={title}
                  pageUrl={PAGE_URL}
                  isOpen={isStateCardOpen}
                  onClose={this.closeStateCard}
                  isAbsolutelyPositioned={!isMobile}
                  isModal={isMobile}
                  closeOnClickOutside
                >
                  <StateCardVehiclesContent stateData={selectedStateData} />
                </StateCard>
              )}
            </Col>
            <Col xs={12} xl={3}>
              {isVehicleViewEnabled && (
                <MapLegend
                  title="Best selling cars"
                  selectedOption={selectedVehicle}
                  onOptionClick={this.onVehicleChange}
                  options={vehiclesOptions}
                />
              )}
              {!showStateCardAboveMap && (
                <StateCard
                  cardTitle={selectedStateName}
                  stateData={selectedStateData}
                  pageTitle={title}
                  pageUrl={PAGE_URL}
                  isOpen={isStateCardOpen}
                  onClose={this.closeStateCard}
                  isAbsolutelyPositioned={false}
                  closeOnClickOutside={false}
                >
                  <StateCardVehiclesContent stateData={selectedStateData} />
                </StateCard>
              )}
            </Col>
          </Row>
        </Container>
        {!isWidget && (
          <Fragment>
            <Insights content={content.child('insights')} pageUrl={PAGE_URL} />
            <Container className="mt-2 mb-3">
              <NationalSalesList
                title={nationalSalesData.metadata('title').value()}
                vehiclesList={nationalSalesVehiclesList}
              />
              <hr className="mt-1_5 mb-2" />
              <ContentFragment componentToUse="div" classes="page-content">
                {content.child('html-1').content}
              </ContentFragment>
              <ContentFragment componentToUse="div" classes="footnote mt-2">
                {content.child('html-2').content}
              </ContentFragment>
            </Container>
          </Fragment>
        )}
      </main>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  content: bindToContent(BSC_CONTENT_PATH),
};

export const BestSellingCars = connect(mapStateToProps)(connectToModel(BestSellingCarsUI, stateToPropsConfig));
